import { useEffect, useMemo, useState } from 'react'
import { Platform, View } from 'react-native'
import styled, { useTheme } from 'styled-components'
import { useUpdateEffect } from 'usehooks-ts'
import Icon from '../../../elements/images/Icon'
import { Col } from '../../../elements/layout'
import { useDimensions } from '../../../hooks'
import { useMediaIs } from '../../../style/media'
import messages from '../messages'
import { CaptionsModal } from './CaptionsModal/CaptionsModal'
import { ConfigurablePromos } from './ConfigurablePromos/ConfigurablePromos'
import FeaturedActorList from './FeaturedActors/ListMobile/FeaturedActorList'
import { NextEpisode } from './NextEpisode/NextEpisode.js'
import { NextEpisodeControl } from './NextEpisode/NextEpisodeControl.js'
import { NextRecommendation } from './NextRecommendation/NextRecommendation.js'
import { PlaybackRateModal } from './PlaybackRateModal/PlaybackRateModal'
import Button, { centerBtnSize, smallBtnSize } from './PlayerControls/Button'
import { Controls } from './PlayerControls/PlayerControls'
import { TrickPlayProgressBar } from './TrickPlayProgressBar/TrickPlayProgressBar.js'

const isIOS = Platform.OS === 'ios'

const calcHeight = (playerHeight) => playerHeight / 6.75

const ColCentered = styled(Col)`
  align-items: center;
  justify-content: space-around;
`

const BodyLeft = styled(View)`
  justify-content: space-between;
  align-items: flex-start;
  flex: 1;
`

const BodyTop = styled(View)`
  flex: 1;
  width: 100%;
`

const BodyBottom = styled(View)`
  flex: 1;
  width: 100%;
  justify-content: flex-end;
  align-items: flex-start;
`

const PromosRight = styled(View)`
  flex: 1;
  justify-content: flex-end;
  align-items: flex-end;
`

const MobilePlayerControls = ({
  type,
  // title,
  // bigPlayButton,
  controlsShown,
  onRequestHideControls,
  onUserActive,
  onRequestClose,
  fullscreen,
  onFullscreenPress,
  onScrubbingStart,
  onScrubbingEnd,
  onSkipBack,
  onSkipForward,
  playing,
  // onPlay,
  onPause,
  onPlayPause,
  // muted,
  // onToggleMute,
  // volume,
  // onVolumeChange,
  subtitles,
  activeSubtitles,
  onSubtitlesChosen,
  creditsReached,
  nextRecommendation,
  nextEpisode,
  actors,
  onActorSelect,
  onViewDetails,
  scrubbingDisabled,
  progressTrackColor,
  playerWidth,
  playerHeight,
  toggleFullChat,

  setPlaybackRate,
  playbackRateOptions,
  playbackRate,

  pauseOverlayLeft,
  pauseOverlayRight,

  filmSlug,
  festivalId,
  // RN-google-cast is not linked in all projects,
  // so the button should be passed in when applicable
  // CastButton,
}) => {
  const [visibleModal, setVisibleModal] = useState(null)
  const toggleModal = (name) => setVisibleModal(visibleModal !== name && name)

  const [watchCredits, setWatchCredits] = useState(false)
  useUpdateEffect(() => {
    if (!creditsReached) setWatchCredits(false) // Reset if scrubbed back from credits
  }, [creditsReached])

  useEffect(() => {
    if (!controlsShown) {
      setVisibleModal(null) // Also close the captions/playback rate when controls were hidden
    }
  }, [controlsShown])

  const theme = useTheme()
  const atLeastTablet = useMediaIs('sm')
  const { landscape } = useDimensions()
  const largeEnoughForFeaturedContent = atLeastTablet || landscape
  const featuredContentShown = type === 'feature' && largeEnoughForFeaturedContent
  const smallIconSize = smallBtnSize(theme.fontSizes.medium, atLeastTablet)
  const centerIconSize = centerBtnSize(theme.fontSizes.medium, atLeastTablet)

  const pauseThen = useMemo(() => {
    const makeHandler =
      (callback) =>
      (...args) => {
        onPause()
        callback(...args)
      }

    return {
      actorSelect: makeHandler(onActorSelect),
    }
  }, [onActorSelect, onPause])

  const visible = controlsShown || creditsReached
  return (
    <Controls
      visible={visible}
      onPress={onUserActive}
      style={{ width: playerWidth, height: playerHeight }}
    >
      <Controls.Header>
        <Controls.Buttons>
          {onRequestClose && (
            <Button
              onPress={onRequestClose}
              fontStyle={{ marginTop: 2 }}
              intlMsg={isIOS ? messages.close : undefined}
            >
              <Icon name="back" size={smallIconSize} />
            </Button>
          )}
        </Controls.Buttons>

        <Controls.Buttons rightAlign>
          {nextEpisode && <NextEpisodeControl nextEpisode={nextEpisode} iconSize={smallIconSize} />}
          {!scrubbingDisabled && setPlaybackRate && (
            <Button onPress={() => toggleModal('playbackRate')}>
              <Icon name="speed" size={smallIconSize} />
            </Button>
          )}
          {!!subtitles?.length && (
            <Button onPress={() => toggleModal('captions')}>
              <Icon name="closed-caption" size={smallIconSize} />
            </Button>
          )}
          {toggleFullChat && (
            <Button onPress={toggleFullChat}>
              <Icon name="chat" size={smallIconSize} />
            </Button>
          )}
          {onFullscreenPress && (
            <Button onPress={onFullscreenPress}>
              <Icon name={fullscreen ? 'fullscreen-exit' : 'fullscreen'} size={smallIconSize} />
            </Button>
          )}
        </Controls.Buttons>
      </Controls.Header>

      <Controls.Body row onPress={() => onRequestHideControls()}>
        <Col size={4}>
          <BodyLeft>
            <BodyTop>
              {featuredContentShown && onActorSelect && (
                <FeaturedActorList actors={actors} onActorSelect={pauseThen.actorSelect} />
              )}
            </BodyTop>
            <BodyBottom>
              {featuredContentShown && (
                <ConfigurablePromos
                  promos={pauseOverlayLeft.slice(0, 1)}
                  filmSlug={filmSlug}
                  festivalId={festivalId}
                  onRequestPause={onPause}
                  position="left"
                />
              )}
            </BodyBottom>
          </BodyLeft>
        </Col>

        <ColCentered size={6} pointerEvents="box-none">
          <Controls.Buttons style={{ flexDirection: !theme.isRTL ? 'row' : 'row-reverse' }}>
            {!scrubbingDisabled && (
              <Button onPress={onSkipBack}>
                <Icon name="replay10" size={centerIconSize} />
              </Button>
            )}

            <Button onPress={onPlayPause}>
              <Icon name={playing ? 'pause' : 'play'} size={centerIconSize} />
            </Button>

            {!scrubbingDisabled && (
              <Button onPress={onSkipForward}>
                <Icon name="forward10" size={centerIconSize} />
              </Button>
            )}
          </Controls.Buttons>
        </ColCentered>

        <Col size={4}>
          {visibleModal === 'playbackRate' && (
            <PlaybackRateModal
              playbackRate={playbackRate}
              playbackRateOptions={playbackRateOptions}
              setPlaybackRate={setPlaybackRate}
              onRequestClose={() => setVisibleModal(null)}
              rightOffset={onFullscreenPress ? smallIconSize * 1.5 : 0}
            />
          )}
          {visibleModal === 'captions' && (
            <CaptionsModal
              subtitles={subtitles}
              activeSubtitles={activeSubtitles}
              onSubtitlesChosen={onSubtitlesChosen}
              onRequestClose={() => setVisibleModal(null)}
              rightOffset={onFullscreenPress ? smallIconSize * 1.5 : 0}
            />
          )}
          <PromosRight>
            {featuredContentShown && (
              <ConfigurablePromos
                promos={pauseOverlayRight.slice(0, 2)}
                filmSlug={filmSlug}
                festivalId={festivalId}
                onRequestPause={onPause}
                position="right"
              />
            )}
          </PromosRight>

          {creditsReached && !nextEpisode && nextRecommendation && (
            <NextRecommendation
              nextRecommendation={nextRecommendation}
              onViewDetails={onViewDetails}
              posterWidth={80}
            />
          )}

          {creditsReached && nextEpisode && !watchCredits && (
            <NextEpisode
              nextEpisode={nextEpisode}
              onWatchCredits={() => setWatchCredits(true)}
              size="small"
            />
          )}
        </Col>
      </Controls.Body>

      <Controls.Footer>
        <TrickPlayProgressBar
          tapToSeek
          trickPlayOnScrub
          inverted={theme.isRTL}
          onSlidingStart={onScrubbingStart}
          onSlidingComplete={onScrubbingEnd}
          onSlidingChange={onUserActive}
          scrubbingDisabled={scrubbingDisabled}
          progressTrackColor={progressTrackColor}
        />
      </Controls.Footer>
    </Controls>
  )
}

MobilePlayerControls.calcHeight = calcHeight

MobilePlayerControls.controlsHeight = 30

export default MobilePlayerControls
